/**
 * Theme definitions for Black Wall.
 * Cyberpunk 2077 inspired color palettes.
 */

export interface ThemeColors {
  bg: string;
  fg: string;
  accent: string;
  accent2: string;
  warn: string;
  danger: string;
  success: string;
  muted: string;
}

/**
 * Universo narrativo del tema.
 *
 * No es decoración: decide el arte, el vocabulario y las metáforas que ve
 * el usuario. Netrunner es cyberpunk y tiene que serlo hasta el final —
 * un reloj de bolsillo victoriano en un tema del Net es un bug, igual que
 * lo sería una scanline CRT en el Club de Tarot.
 *
 * Cada universo trae su propio juego de ilustraciones y su propio léxico
 * (ver themes/lexicon.ts), del mismo modo que THEME_PHRASES ya daba a cada
 * tema sus frases de espera en el chat.
 */
export type Universe = "cyberpunk" | "arcane";

/**
 * Ornamento de esquina que usa la piel. No es color: es la forma con la
 * que un panel se remata, y es parte de por qué dos temas se sienten
 * distintos aunque compartan layout.
 *   bracket  — corchete técnico corpo (default cyberpunk)
 *   filigree — filigrana victoriana (Niebla Gris)
 *   none     — sin remate (temas claros / minimal)
 */
export type Ornament = "bracket" | "filigree" | "none";

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  isDark: boolean;
  /** Default: "cyberpunk". */
  universe?: Universe;
  /** Default: "bracket". */
  ornament?: Ornament;
}

export const themes: Theme[] = [
  {
    id: "netrunner-cyan",
    name: "Netrunner",
    description: "Cyan eléctrico — estilo hacker del Net",
    colors: {
      bg: "#0a0a0f",
      fg: "#e4e4f0",
      accent: "#00f0ff",
      accent2: "#0066ff",
      warn: "#fcee0a",
      danger: "#ff003c",
      success: "#00ff9c",
      muted: "#6a6a7e",
    },
    isDark: true,
  },
  {
    id: "militech-corpo",
    name: "Militech",
    description: "Amarillo Militech — eficiencia corporativa",
    colors: {
      bg: "#0f0f08",
      fg: "#f5f0d4",
      accent: "#fcee0a",
      accent2: "#ff8800",
      warn: "#ff8800",
      danger: "#cc0000",
      success: "#88dd00",
      muted: "#7a7260",
    },
    isDark: true,
  },
  {
    id: "arasaka-red",
    name: "Arasaka",
    description: "Rojo Arasaka — poder japonés",
    colors: {
      bg: "#0a0000",
      fg: "#ffdddd",
      accent: "#ff003c",
      accent2: "#ff4466",
      warn: "#ffaa00",
      danger: "#ff0000",
      success: "#00aa66",
      muted: "#882222",
    },
    isDark: true,
  },
  {
    id: "ncpd-terminal",
    name: "NCPD Terminal",
    description: "Terminal fosforescente — old school",
    colors: {
      bg: "#000505",
      fg: "#00ff00",
      accent: "#00ff00",
      accent2: "#66ff66",
      warn: "#ffff00",
      danger: "#ff3300",
      success: "#00ff00",
      muted: "#448844",
    },
    isDark: true,
  },
  {
    id: "kiroshi-optics",
    name: "Kiroshi Optics",
    description: "Tema claro — clínica de ripperdoc",
    colors: {
      bg: "#f0f4f8",
      fg: "#0a1020",
      accent: "#0066cc",
      accent2: "#00aaff",
      warn: "#ff8800",
      danger: "#cc0044",
      success: "#00aa66",
      muted: "#6a7a8a",
    },
    isDark: false,
    ornament: "none",
  },
  {
    id: "night-city",
    name: "Night City",
    description: "Rojo maroon — directo del juego, choom",
    colors: {
      bg: "#120808",
      fg: "#e8dcd0",
      accent: "#e84545",
      accent2: "#00e5d8",
      warn: "#e8c020",
      danger: "#cc2222",
      success: "#aaee00",
      muted: "#7a5c5c",
    },
    isDark: true,
  },
  {
    id: "rogue-ai",
    name: "Rogue AI",
    description: "Magenta glitch — más allá del Blackwall",
    colors: {
      bg: "#000000",
      fg: "#cccccc",
      accent: "#ff00ff",
      accent2: "#aa00ff",
      warn: "#ff6600",
      danger: "#ff0066",
      success: "#00ffaa",
      muted: "#666666",
    },
    isDark: true,
  },
  {
    id: "gray-fog",
    name: "Niebla Gris",
    description: "Oro y carmesí — el Club de Tarot sobre la niebla",
    colors: {
      bg: "#0b0a10",
      fg: "#e6dcc6",
      accent: "#d8a24a",
      accent2: "#b3283c",
      warn: "#e0912f",
      danger: "#c1122f",
      success: "#8fae6e",
      muted: "#7c7789",
    },
    isDark: true,
    universe: "arcane",
    ornament: "filigree",
  },
];

export const DEFAULT_THEME = "netrunner-cyan";

export function getTheme(id: string): Theme | undefined {
  return themes.find((t) => t.id === id);
}

export function getUniverse(theme: Theme): Universe {
  return theme.universe ?? "cyberpunk";
}
